/// <reference path="../DataTypes/types.js" />
import SettingsService from './SettingsService';

// Constants
const DEFAULT_TITAN_EMAIL = 'addressnotprovided@titancontainer.dk';
const VERIFICATION_POLLING_INTERVAL_MS = 5000; // 5 seconds
const MAX_VERIFICATION_ATTEMPTS = 12;
const API_BASE_URL =
  process.env.REACT_APP_CONSUMER_API_URL + '/api/CheckoutApi';

class BookingService {
  constructor() {
    this.paymentService = null;
    this.executeBookingFlowInProgress = false;
  }

  setPaymentService(paymentService) {
    this.paymentService = paymentService;
  }

  /**
   * Build debtor account payload from user data
   */
  buildDebtorAccountPayload(user) {
    return {
      userId: user.stripeCustomerId ?? '',
      userEmail: user.email,
      userName: user.firstName + ' ' + user.lastName,
      phone: user.phone,
      country: user.address.country,
      countryShortName: SettingsService.getCountryShortCode(),
      cvr: user.cvr,
      city: user.address.city,
      line1: user.address.streetAddress,
      state: user.address.state,
      postalCode: user.address.zipCode,
    };
  }

  /**
   * Build booking order payload from booking data
   */
  buildBookingOrderPayload(debtor, user, room, currentLocation, dateFrom) {
    return {
      debtorAccountId: debtor.debtorAccountId,
      countryShortName: SettingsService.getCountryShortCode(),
      roomId: room.Id,
      dateFrom: dateFrom,
      userEmail: user.email,
      phone: user.phone,
      titanEmail: currentLocation.email || DEFAULT_TITAN_EMAIL,
      containerNumber: room.Number,
      containerPrefix: room.Prefix,
      locationName: currentLocation.name,
    };
  }

  /**
   * Create or get existing debtor account
   */
  async createDebtorAccount(user, onStatusUpdate) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    onStatusUpdate?.('loading_text_booking_creation');
    const debtorPayload = this.buildDebtorAccountPayload(user);

    const debtor =
      await this.paymentService.GetOrCreateDebtorAccount(debtorPayload);

    if (!debtor) {
      onStatusUpdate?.('error_creating_debtor_account');
      throw new Error('Debtor account creation failed');
    }

    return debtor;
  }

  /**
   * Create a booking order
   */
  async createOrder(debtor, user, room, currentLocation, dateFrom) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    const orderPayload = this.buildBookingOrderPayload(
      debtor,
      user,
      room,
      currentLocation,
      dateFrom
    );

    const order = await this.paymentService.CreateBookingOrder(orderPayload);
    return order;
  }

  /**
   * Start Stripe identity verification
   */
  async startVerification(stripePromise, sessionClientSecret) {
    const stripe = await stripePromise;
    const { error } = await stripe.verifyIdentity(sessionClientSecret);

    if (error) {
      return { error };
    }

    return { error: null };
  }

  /**
   * Poll verification session status until completion or max attempts reached
   * Returns: { verified: boolean, requiredInput?: string }
   */
  async pollVerificationStatus(session) {
    if (!this.paymentService) {
      throw new Error('PaymentService not set');
    }

    let checks = 0;
    return new Promise((resolve, reject) => {
      const intervalId = setInterval(async () => {
        try {
          checks++;

          if (checks > MAX_VERIFICATION_ATTEMPTS) {
            clearInterval(intervalId);
            resolve({ verified: false, timedOut: true });
            return;
          }

          const responseData =
            await this.paymentService.checkVerificationSessionStatus(
              session.verificationSessionId,
              session.userEmail
            );

          if (
            responseData.verificationStatus &&
            responseData.isProcessing === false
          ) {
            clearInterval(intervalId);
            resolve({ verified: true });
          } else if (responseData.requiredUserInput) {
            clearInterval(intervalId);
            resolve({
              verified: false,
              requiredInput: responseData.requiredUserInput,
            });
          }
        } catch (error) {
          clearInterval(intervalId);
          reject(error);
        }
      }, VERIFICATION_POLLING_INTERVAL_MS);
    });
  }

  /**
   * Handle the verification flow (create session, start verification, poll status)
   */
  async handleVerificationFlow(
    user,
    order,
    room,
    dateFrom,
    titanEmail,
    debtor,
    stripeUserId,
    stripePromise,
    currentLocation,
    onStatusUpdate
  ) {
    const session = await this.paymentService.createVerificationSession(
      user.email,
      titanEmail,
      order.orderId,
      room.Id,
      dateFrom,
      debtor.tcrsDebtorId,
      currentLocation.name,
      stripeUserId
    );

    if (!session.clientSecret) {
      throw new Error('No client secret received from verification session');
    }

    // Step 2: Start verification
    const verifyResult = await this.startVerification(
      stripePromise,
      session.clientSecret
    );

    if (verifyResult.error) {
      return {
        success: false,
        error: 'User closed verification',
        debtor,
        order,
      };
    }

    // Step 3: Poll verification status
    onStatusUpdate('loading_text_verification_submitted');
    const pollResult = await this.pollVerificationStatus({
      ...session,
      userEmail: user.email,
    });

    if (pollResult.verified) {
      return { success: true, order, debtor };
    } else if (pollResult.timedOut) {
      try {
        await this.paymentService.triggerVerificationTimeoutEmail({
          orderId: order.orderId,
          user,
          titanEmail,
        });
      } catch (error) {
        console.warn('Failed to trigger verification timeout email', error);
      }

      return { success: false, timedOut: true, debtor, order };
    } else if (pollResult.requiredInput) {
      await this.paymentService.triggerVerificationTimeoutEmail({
          orderId: order.orderId,
          user,
          titanEmail,
        });
      return {
        success: false,
        requiredInput: pollResult.requiredInput,
        debtor,
        order,
      };
    } else {
      return { success: false, error: 'Verification failed', debtor, order };
    }
  }

  async executeBookingFlow(
    user,
    setUser,
    room,
    currentLocation,
    dateFrom,
    existingDebtor = null,
    existingOrder = null,
    setDebtor,
    setExistingOrder,
    onStatusUpdate = () => {}
  ) {
    //Check that a booking is not allready in progress to avoid multiple bookings being created when user clicks multiple times on the booking button
    if (this.executeBookingFlowInProgress) {
      return {
        success: false,
        status: 'in_progress',
        error: 'Booking flow already in progress',
      };
    }

    this.executeBookingFlowInProgress = true;

    try {
      // Initialize or reuse debtor account
      let debtor = existingDebtor;

      // Check if the debtor account exists
      if (!debtor) {
        // Update the UI text to indicate that the debtor account is being created
        onStatusUpdate('loading_text_booking_creation');

        //Create a new debtor account if it doesn't exist and update the state
        debtor = await this.createDebtorAccount(user, onStatusUpdate);

        // Update the state with the new debtor account
        setDebtor(debtor);
      }

      //Check that the debtor account has an associated Stripe customer ID before proceeding, if not return an error to be handled in the UI
      if (!debtor.stripeUserId) {
        return {
          success: false,
          error: 'Debtor account does not have associated Stripe customer ID',
          status: 'debtor_account_issue',
          debtor,
          order: existingOrder,
        };
      }

      //Set the Stripe customer ID on the user state to ensure it's available for the verification flow and to avoid issues if the user tries to book again after a failed verification attempt
      setUser({
        ...user,
        stripeCustomerId: debtor.stripeUserId,
      });

      // Initialize or reuse booking order
      let order = existingOrder;
      if (!order) {
        // Create a new booking order if it doesn't exist and update the state
        order = await this.createOrder(
          debtor,
          user,
          room,
          currentLocation,
          dateFrom
        );

        // Update the state with the new booking order
        setExistingOrder(order);
      }

      // Early return if user is already verified with a status of allready verified to avoid unnecessary verification flow and to allow the UI to handle this case specifically if needed
      if (user.isIdentificationVerified) {
        return {
          success: true,
          status: 'already_verified',
          order,
          debtor,
        };
      }

      // Start the verification flow (create session, start verification, poll status)
      return {
        success: true,
        status: 'requires_verification',
        order,
        debtor,
      };
    } catch (error) {
      return { success: false, error: error.message };
    } finally {
      this.executeBookingFlowInProgress = false;
    }
  }
}

const bookingService = new BookingService();

export default bookingService;
